import axios from "axios";
import { DESIGNMATE } from "../../../config/endPoints";

// Helper function to get auth token
const getAuthToken = () => localStorage.getItem("authorization");

// Helper function to convert File to base64
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Helper function to convert Blob to base64 (for drawing modal masks)
const convertBlobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// 1. Remove Background API
export const removeBackground = async (image, options = {}) => {
  const token = getAuthToken();
  let requestData = {};
  
  // Handle different image input types
  if (image instanceof File) {
    // Direct File object handling
    const base64 = await convertFileToBase64(image);
    const ext = image.name.split(".").pop();
    requestData = { file: `data:image/${ext};base64,${base64}` };
  } else if (typeof image === 'string' && image.startsWith('data:')) {
    // Already a data URL
    requestData = { file: image };
  } else if (typeof image === 'string') {
    // Regular URL
    requestData = { image_url: image };
  } else {
    console.error("Unsupported image type", image);
    throw new Error("Unsupported image format");
  }
  
  console.log("API Request data type:", Object.keys(requestData)[0]);
  
  // Add additional options
  Object.assign(requestData, options);
  
  const response = await axios.post(
    `${DESIGNMATE}/edit/removeBackground`,
    requestData,
    {
      headers: {
        "Content-Type": "application/json",
        authorization: token,
      },
    }
  );
  
  console.log("Remove Background API response:", response.data);
  return response.data;
};

// 2. Blur Background API
// Blur Background API
export const blurBackground = async (image, options = {}) => {
  const token = getAuthToken();
  let requestData = {};
  
  // Debug to see what we're getting
  console.log("blurBackground input type:", typeof image);
  console.log("Is File object?", image instanceof File);
  console.log("Is data URL?", typeof image === 'string' && image.startsWith('data:'));
  
  // Handle different image input types
  if (image instanceof File) {
    // Direct File object handling
    const base64 = await convertFileToBase64(image);
    const ext = image.name.split(".").pop();
    requestData = { file: `data:image/${ext};base64,${base64}` };
  } else if (typeof image === 'string' && image.startsWith('data:')) {
    // Already a data URL
    requestData = { file: image };
  } else if (typeof image === 'string') {
    // Regular URL
    requestData = { image_url: image };
  } else {
    console.error("Unsupported image type", image);
    throw new Error("Unsupported image format");
  }
  
  // Add scale parameter - this is what the backend expects
  requestData.scale = options.scale || 5;
  
  console.log("Blur API Request data:", requestData);
  
  const response = await axios.post(
    `${DESIGNMATE}/edit/blurBackground`,
    requestData,
    {
      headers: {
        "Content-Type": "application/json",
        authorization: token,
      },
    }
  );
  
  console.log("Blur Background API response:", response.data);
  return response.data;
};

// 3. Generate Background API
// Updated generateBackground function for editUtils.js

export const generateBackground = async (image, options = {}) => {
  const token = getAuthToken();
  let requestData = {};
  
  // Handle different image input types (main image)
  if (image instanceof File) {
    // Direct File object handling
    const base64 = await convertFileToBase64(image);
    const ext = image.name.split(".").pop();
    requestData = { file: `data:image/${ext};base64,${base64}` };
  } else if (typeof image === 'string' && image.startsWith('data:')) {
    // Already a data URL
    requestData = { file: image };
  } else if (typeof image === 'string') {
    // Regular URL
    requestData = { image_url: image };
  } else {
    console.error("Unsupported image type", image);
    throw new Error("Unsupported image format");
  }
  
  // Process reference image if provided in options
  if (options.ref_image_file instanceof File) {
    const refBase64 = await convertFileToBase64(options.ref_image_file);
    const refExt = options.ref_image_file.name.split(".").pop();
    requestData.ref_image_file = `data:image/${refExt};base64,${refBase64}`;
    delete options.ref_image_file; // Remove original file object
  }
  
  // Clean up options object before merging
  const cleanOptions = { ...options };
  
  // Ensure these are properly formatted for the API
  if (cleanOptions.fast !== undefined) {
    cleanOptions.fast = Boolean(cleanOptions.fast);
  }
  
  if (cleanOptions.refine_prompt !== undefined) {
    cleanOptions.refine_prompt = Boolean(cleanOptions.refine_prompt);
  }
  
  if (cleanOptions.enhance_ref_image !== undefined) {
    cleanOptions.enhance_ref_image = Boolean(cleanOptions.enhance_ref_image);
  }
  
  if (cleanOptions.num_results !== undefined) {
    cleanOptions.num_results = Number(cleanOptions.num_results);
  }
  
  // Add all cleaned options to request data
  for (const key in cleanOptions) {
    // Skip the already processed ref_image_file
    if (key === 'ref_image_file') continue;
    
    // Ensure we don't overwrite image_url or file that was set above
    if (!(key === 'image_url' && requestData.file) && 
        !(key === 'file' && requestData.image_url)) {
      requestData[key] = cleanOptions[key];
    }
  }
  
  // Log the full request data for debugging
  console.log("Generate Background API request:", requestData);
  
  const response = await axios.post(
    `${DESIGNMATE}/edit/generateBackground`,
    requestData,
    {
      headers: {
        "Content-Type": "application/json",
        authorization: token,
      },
    }
  );
  console.log("Generate Background API response:", response.data);
  return response.data;
};

// 4. Increase Resolution API
export const increaseResolution = async (image, options = {}) => {
  const token = getAuthToken();
  let requestData = {};
  
  // Handle different image input types
  if (image instanceof File) {
    // Direct File object handling
    const base64 = await convertFileToBase64(image);
    const ext = image.name.split(".").pop();
    requestData = { file: `data:image/${ext};base64,${base64}` };
  } else if (typeof image === 'string' && image.startsWith('data:')) {
    // Already a data URL
    requestData = { file: image };
  } else if (typeof image === 'string') {
    // Regular URL
    requestData = { image_url: image };
  } else {
    console.error("Unsupported image type", image);
    throw new Error("Unsupported image format");
  }
  
  // Add required parameters according to API documentation
  if (options.desired_increase) {
    requestData.desired_increase = options.desired_increase;
  }
  
  // Add additional parameters for our UI enhancements
  // These might not be used by the actual API but are supported by our frontend
  if (options.preserve_details !== undefined) {
    requestData.preserve_details = Boolean(options.preserve_details);
  }
  
  if (options.reduce_noise !== undefined) {
    requestData.reduce_noise = Boolean(options.reduce_noise);
  }
  
  if (options.sharpen_edges !== undefined) {
    requestData.sharpen_edges = Boolean(options.sharpen_edges);
  }
  
  console.log("Increase Resolution API request data:", requestData);
  
  const response = await axios.post(
    `${DESIGNMATE}/edit/increaseResolution`,
    requestData,
    {
      headers: {
        "Content-Type": "application/json",
        authorization: token,
      },
    }
  );
  
  console.log("Increase Resolution API response:", response.data);
  return response.data;
};

// 5. Crop Foreground API
export const cropForeground = async (image, options = {}) => {
  const token = getAuthToken();
  let requestData = {};
  
  // Handle different image input types
  if (image instanceof File) {
    // Direct File object handling
    const base64 = await convertFileToBase64(image);
    const ext = image.name.split(".").pop();
    requestData = { file: `data:image/${ext};base64,${base64}` };
  } else if (typeof image === 'string' && image.startsWith('data:')) {
    // Already a data URL
    requestData = { file: image };
  } else if (typeof image === 'string') {
    // Regular URL
    requestData = { image_url: image };
  } else {
    console.error("Unsupported image type", image);
    throw new Error("Unsupported image format");
  }
  
  // Add padding parameter (integer)
  if (options.padding !== undefined) {
    requestData.padding = Number(options.padding);
  }
  
  // Add force_rmbg parameter (boolean)
  if (options.force_rmbg !== undefined) {
    requestData.force_rmbg = Boolean(options.force_rmbg);
  }
  
  console.log("Crop Foreground API request data:", requestData);
  
  const response = await axios.post(
    `${DESIGNMATE}/edit/cropForeground`,
    requestData,
    {
      headers: {
        "Content-Type": "application/json",
        authorization: token,
      },
    }
  );
  
  console.log("Crop Foreground API response:", response.data);
  return response.data;
};

// 6. Erase Foreground API
export const eraseForeground = async (image, options = {}) => {
  const token = getAuthToken();
  let requestData = {};
  
  // Handle different image input types
  if (image instanceof File) {
    // Direct File object handling
    const base64 = await convertFileToBase64(image);
    const ext = image.name.split(".").pop();
    requestData = { file: `data:image/${ext};base64,${base64}` };
  } else if (typeof image === 'string' && image.startsWith('data:')) {
    // Already a data URL
    requestData = { file: image };
  } else if (typeof image === 'string') {
    // Regular URL
    requestData = { image_url: image };
  } else {
    console.error("Unsupported image type", image);
    throw new Error("Unsupported image format");
  }
  
  console.log("Erase Foreground API request data:", requestData);
  
  const response = await axios.post(
    `${DESIGNMATE}/edit/eraseForeground`,
    requestData,
    {
      headers: {
        "Content-Type": "application/json",
        authorization: token,
      },
    }
  );
  
  console.log("Erase Foreground API response:", response.data);
  return response.data;
};

// 7. Erase Element API
export const eraseElement = async (image, options = {}) => {
  const token = getAuthToken();
  let requestData = {};
  
  console.log("=== ERASE ELEMENT DEBUG ===");
  console.log("Input image type:", typeof image);
  console.log("Input image:", image instanceof File ? "File object" : image instanceof Blob ? "Blob object" : image);
  console.log("Input options:", options);
  
  // Handle different image input types
  if (image instanceof File) {
    // Direct File object handling
    const base64 = await convertFileToBase64(image);
    const ext = image.name.split(".").pop();
    requestData = { file: `data:image/${ext};base64,${base64}` };
    console.log("Using File object, converted to base64");
  } else if (typeof image === 'string' && image.startsWith('data:')) {
    // Already a data URL
    requestData = { file: image };
    console.log("Using data URL directly");
  } else if (typeof image === 'string') {
    // Regular URL
    requestData = { image_url: image };
    console.log("Using image URL");
  } else {
    console.error("Unsupported image type", image);
    throw new Error("Unsupported image format");
  }
  
  // Handle mask input types - UPDATED to handle Blob objects from drawing modal
  if (options.mask_file instanceof Blob) {
    // Mask is a Blob object (from drawing modal)
    console.log("Converting Blob mask to base64...");
    const maskBase64 = await convertBlobToBase64(options.mask_file);
    requestData.mask_file = `data:image/png;base64,${maskBase64}`;
    console.log("Blob mask converted successfully");
  } else if (options.mask_file instanceof File) {
    // Mask is a File object
    console.log("Converting File mask to base64...");
    const maskBase64 = await convertFileToBase64(options.mask_file);
    const maskExt = options.mask_file.name.split(".").pop();
    requestData.mask_file = `data:image/${maskExt};base64,${maskBase64}`;
    console.log("File mask converted successfully");
  } else if (typeof options.mask_file === 'string' && options.mask_file.startsWith('data:')) {
    // Mask is already a data URL
    requestData.mask_file = options.mask_file;
    console.log("Using mask data URL directly");
  } else if (options.mask_url) {
    // Mask URL provided
    requestData.mask_url = options.mask_url;
    console.log("Using mask URL");
  }
  
  // Add mask_type parameter (default: "manual")
  requestData.mask_type = options.mask_type || "manual";
  
  console.log("Final request structure:");
  console.log("- Has main image:", requestData.file ? "file (base64)" : requestData.image_url ? "image_url" : "MISSING");
  console.log("- Has mask:", requestData.mask_file ? "mask_file (base64)" : requestData.mask_url ? "mask_url" : "MISSING");
  console.log("- Mask type:", requestData.mask_type);
  console.log("Full request data:", requestData);
  
  const response = await axios.post(
    `${DESIGNMATE}/edit/eraseElement`,
    requestData,
    {
      headers: {
        "Content-Type": "application/json",
        authorization: token,
      },
    }
  );
  
  console.log("Erase Element API response:", response.data);
  return response.data;
};

// 8. Generative Fill API
export const generativeFill = async (image, options = {}) => {
  const token = getAuthToken();
  let requestData = {};
  
  console.log("=== GENERATIVE FILL DEBUG ===");
  console.log("Input image type:", typeof image);
  console.log("Input image:", image instanceof File ? "File object" : image instanceof Blob ? "Blob object" : image);
  console.log("Input options:", options);
  
  // Handle different image input types
  if (image instanceof File) {
    // Direct File object handling
    const base64 = await convertFileToBase64(image);
    const ext = image.name.split(".").pop();
    requestData = { file: `data:image/${ext};base64,${base64}` };
    console.log("Using File object, converted to base64");
  } else if (typeof image === 'string' && image.startsWith('data:')) {
    // Already a data URL
    requestData = { file: image };
    console.log("Using data URL directly");
  } else if (typeof image === 'string') {
    // Regular URL
    requestData = { image_url: image };
    console.log("Using image URL");
  } else {
    console.error("Unsupported image type", image);
    throw new Error("Unsupported image format");
  }
  
  // Handle mask input types - UPDATED to handle Blob objects from drawing modal
  if (options.mask_file instanceof Blob) {
    // Mask is a Blob object (from drawing modal)
    console.log("Converting Blob mask to base64...");
    const maskBase64 = await convertBlobToBase64(options.mask_file);
    requestData.mask_file = `data:image/png;base64,${maskBase64}`;
    console.log("Blob mask converted successfully");
  } else if (options.mask_file instanceof File) {
    // Mask is a File object
    console.log("Converting File mask to base64...");
    const maskBase64 = await convertFileToBase64(options.mask_file);
    const maskExt = options.mask_file.name.split(".").pop();
    requestData.mask_file = `data:image/${maskExt};base64,${maskBase64}`;
    console.log("File mask converted successfully");
  } else if (typeof options.mask_file === 'string' && options.mask_file.startsWith('data:')) {
    // Mask is already a data URL
    requestData.mask_file = options.mask_file;
    console.log("Using mask data URL directly");
  } else if (options.mask_url) {
    // Mask URL provided
    requestData.mask_url = options.mask_url;
    console.log("Using mask URL");
  }
  
  // Add mask_type parameter (default: "manual")
  requestData.mask_type = options.mask_type || "manual";
  
  // Add required prompt
  if (options.prompt) {
    requestData.prompt = options.prompt;
  }
  
  // Add optional negative_prompt
  if (options.negative_prompt) {
    requestData.negative_prompt = options.negative_prompt;
  }
  
  // Add num_results parameter (1-4, default 4)
  if (options.num_results !== undefined) {
    requestData.num_results = Math.min(Math.max(parseInt(options.num_results), 1), 4);
  }
  
  // Add sync parameter (boolean, default false)
  if (options.sync !== undefined) {
    requestData.sync = Boolean(options.sync);
  }
  
  // Add seed parameter (optional integer)
  if (options.seed !== undefined) {
    requestData.seed = parseInt(options.seed);
  }
  
  console.log("Final request structure:");
  console.log("- Has main image:", requestData.file ? "file (base64)" : requestData.image_url ? "image_url" : "MISSING");
  console.log("- Has mask:", requestData.mask_file ? "mask_file (base64)" : requestData.mask_url ? "mask_url" : "MISSING");
  console.log("- Prompt:", requestData.prompt || "MISSING");
  console.log("- Negative prompt:", requestData.negative_prompt || "none");
  console.log("- Num results:", requestData.num_results || "default");
  console.log("- Sync:", requestData.sync !== undefined ? requestData.sync : "default");
  console.log("- Seed:", requestData.seed || "none");
  console.log("- Mask type:", requestData.mask_type);
  console.log("Full request data:", requestData);
  
  const response = await axios.post(
    `${DESIGNMATE}/edit/generativeFill`,
    requestData,
    {
      headers: {
        "Content-Type": "application/json",
        authorization: token,
      },
    }
  );
  
  console.log("Generative Fill API response:", response.data);
  return response.data;
};

// Additional edit options can be added following the same pattern 